import type { LngLat } from '../shared/types';
import { bearing, cumulativeDistances, distance, lerp } from '../nav/geo';

/** 位置ソースが出す1サンプル。GPS でも GPX 再生でも同じ形 */
export interface PositionSample {
  position: LngLat;
  /** 精度 [m] */
  accuracy: number;
  /** 速度 [m/s]（取れない場合 null） */
  speed: number | null;
  /** 進行方向 [度]（停車中や取れない場合 null） */
  heading: number | null;
  /** 取得時刻 [ms epoch] */
  timestamp: number;
  /** gps = 実 GPS、replay = GPX 再生、sim = テスト走行（ルートの上を自動で走る） */
  source: 'gps' | 'replay' | 'sim';
}

export interface PositionSource {
  readonly kind: PositionSample['source'];
  /** onError の fatal が true のときは、ソース側で止まっている（呼び出し側も停止扱いにする） */
  start(onSample: (s: PositionSample) => void, onError?: (e: string, fatal?: boolean) => void): void;
  stop(): void;
}

/* ------------------------------------------------------------------ */
/* 実 GPS                                                              */
/* ------------------------------------------------------------------ */

export class GeolocationSource implements PositionSource {
  readonly kind = 'gps' as const;
  private watchId: number | null = null;

  start(onSample: (s: PositionSample) => void, onError?: (e: string, fatal?: boolean) => void): void {
    if (!('geolocation' in navigator)) {
      onError?.('このブラウザは位置情報に対応していません');
      return;
    }
    if (!window.isSecureContext) {
      onError?.('位置情報には HTTPS が必要です（localhost か Pages のURLで開いてください）');
      return;
    }
    this.watchId = navigator.geolocation.watchPosition(
      (p) =>
        onSample({
          position: [p.coords.longitude, p.coords.latitude],
          accuracy: p.coords.accuracy,
          speed: p.coords.speed,
          heading: p.coords.heading,
          timestamp: p.timestamp,
          source: 'gps',
        }),
      (e) => {
        if (e.code === e.PERMISSION_DENIED) {
          // 拒否は再試行しても変わらないので止める（止めないとエラーが出続ける）
          this.stop();
          onError?.(`位置情報が許可されていません。${permissionHelp()}`, true);
          return;
        }
        onError?.(`位置情報エラー (${e.code}): ${e.message}`);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );
  }

  stop(): void {
    if (this.watchId != null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }
}

/**
 * 位置情報の許可の状態。Permissions API が無い環境では 'unknown'。
 * 起動時に自動で GPS を始めてよいか（拒否されていないか）の判断に使う
 */
export async function geolocationPermission(): Promise<PermissionState | 'unknown'> {
  try {
    return (await navigator.permissions.query({ name: 'geolocation' })).state;
  } catch {
    return 'unknown';
  }
}

/** 位置情報が拒否されたときの直し方（ブラウザごとに設定の場所が違う） */
function permissionHelp(): string {
  const ua = navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
  const standalone = matchMedia('(display-mode: standalone)').matches;
  if (ios && !standalone && /CriOS/.test(ua)) {
    return '「設定 → プライバシーとセキュリティ → 位置情報サービス → Chrome」を「このAppの使用中」にして「正確な位置情報」をオンにし、ページを再読み込みしてから「GPS」を押して「許可」を選んでください。';
  }
  if (ios) {
    return '「設定 → プライバシーとセキュリティ → 位置情報サービス → Safari の Web サイト」を「このAppの使用中」にし、Safari の「Web サイトの設定」でこのサイトの位置情報を「許可」にしてください。';
  }
  return 'ブラウザのサイト設定で、このサイトの位置情報を「許可」にしてください。';
}

/* ------------------------------------------------------------------ */
/* GPX 再生（机上開発用シミュレーター）                                */
/* ------------------------------------------------------------------ */

interface TrackPoint {
  position: LngLat;
  time: number | null;
}

export function parseGpx(xml: string): TrackPoint[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.querySelector('parsererror')) throw new Error('GPX の解析に失敗しました');
  const nodes = Array.from(doc.querySelectorAll('trkpt, rtept'));
  const pts = nodes.map((n) => {
    const t = n.querySelector('time')?.textContent;
    return {
      position: [Number(n.getAttribute('lon')), Number(n.getAttribute('lat'))] as LngLat,
      time: t ? Date.parse(t) : null,
    };
  });
  if (pts.length < 2) throw new Error('GPX に2点以上のトラックポイントがありません');
  return pts;
}

/**
 * GPX のトラックを時間どおり（× speedFactor）に再生する。
 * <time> が無い GPX は fallbackSpeed [m/s] で等速走行させる。
 * jitter [m] を指定すると GPS っぽいブレを足せる（スナップ・逸脱判定のテスト用）。
 */
export class GpxReplaySource implements PositionSource {
  readonly kind = 'replay' as const;
  private timer: number | null = null;
  private readonly times: number[];

  constructor(
    private readonly points: TrackPoint[],
    private readonly opts = { speedFactor: 1, fallbackSpeed: 11, intervalMs: 1000, jitter: 0 },
  ) {
    // 各点の相対時刻 [ms] を用意
    this.times = [0];
    for (let i = 1; i < points.length; i++) {
      const a = points[i - 1], b = points[i];
      const dt =
        a.time != null && b.time != null && b.time > a.time
          ? b.time - a.time
          : (distance(a.position, b.position) / opts.fallbackSpeed) * 1000;
      this.times.push(this.times[i - 1] + dt);
    }
  }

  get durationMs(): number {
    return this.times[this.times.length - 1];
  }

  start(onSample: (s: PositionSample) => void): void {
    this.stop();
    const t0 = performance.now();
    const tick = () => {
      const t = (performance.now() - t0) * this.opts.speedFactor;
      const s = this.sampleAt(t);
      onSample(s);
      if (t >= this.durationMs) this.stop();
    };
    tick();
    this.timer = window.setInterval(tick, this.opts.intervalMs);
  }

  stop(): void {
    if (this.timer != null) clearInterval(this.timer);
    this.timer = null;
  }

  private sampleAt(t: number): PositionSample {
    const tt = Math.min(t, this.durationMs);
    let i = 0;
    while (i < this.times.length - 2 && this.times[i + 1] < tt) i++;
    const a = this.points[i], b = this.points[i + 1];
    const seg = this.times[i + 1] - this.times[i];
    const f = seg === 0 ? 0 : (tt - this.times[i]) / seg;
    let pos = lerp(a.position, b.position, f);
    if (this.opts.jitter > 0) pos = addJitter(pos, this.opts.jitter);
    const segDist = distance(a.position, b.position);
    const speed = seg > 0 && t < this.durationMs ? segDist / (seg / 1000) : 0;
    return {
      position: pos,
      accuracy: Math.max(5, this.opts.jitter),
      speed,
      heading: speed > 0 ? bearing(a.position, b.position) : null,
      timestamp: Date.now(),
      source: 'replay',
    };
  }
}

/* ------------------------------------------------------------------ */
/* テスト走行（今のルートの上を自動で走る。実走できないときの開発・確認用）   */
/* ------------------------------------------------------------------ */

export interface RouteSimOptions {
  /** 何倍速で走るか（表示する速度は実際の速度のまま） */
  speedFactor: number;
  /** 一般道の速度 [km/h] */
  localKmh: number;
  /** 高速道路の速度 [km/h] */
  highwayKmh: number;
  intervalMs: number;
  /** GPS っぽいブレ [m] */
  jitter: number;
}

export class RouteSimSource implements PositionSource {
  readonly kind = 'sim' as const;
  private timer: number | null = null;
  private along = 0;
  private readonly cum: number[];
  /** 高速区間（始点からの距離 [m]） */
  private readonly highwayRanges: [number, number][];
  private readonly opts: RouteSimOptions;

  constructor(
    private readonly line: LngLat[],
    highways: [number, number][] = [],
    opts: Partial<RouteSimOptions> = {},
  ) {
    this.opts = { speedFactor: 4, localKmh: 40, highwayKmh: 100, intervalMs: 1000, jitter: 3, ...opts };
    this.cum = cumulativeDistances(line);
    this.highwayRanges = highways.map(([a, b]) => [this.cum[a] ?? 0, this.cum[Math.min(b, this.cum.length - 1)]]);
  }

  get speedFactor(): number {
    return this.opts.speedFactor;
  }

  setSpeedFactor(f: number): void {
    this.opts.speedFactor = f;
  }

  start(onSample: (s: PositionSample) => void): void {
    this.stop();
    const total = this.cum[this.cum.length - 1];
    const tick = () => {
      const v = this.speedAt(this.along); // [m/s]
      const done = this.along >= total;
      onSample(this.sampleAt(this.along, done ? 0 : v));
      if (done) return this.stop();
      this.along = Math.min(total, this.along + v * this.opts.speedFactor * (this.opts.intervalMs / 1000));
    };
    tick();
    this.timer = window.setInterval(tick, this.opts.intervalMs);
  }

  stop(): void {
    if (this.timer != null) clearInterval(this.timer);
    this.timer = null;
  }

  private speedAt(along: number): number {
    const onHighway = this.highwayRanges.some(([a, b]) => along >= a && along < b);
    return ((onHighway ? this.opts.highwayKmh : this.opts.localKmh) * 1000) / 3600;
  }

  private sampleAt(along: number, speed: number): PositionSample {
    let i = 0;
    while (i < this.cum.length - 2 && this.cum[i + 1] < along) i++;
    const a = this.line[i], b = this.line[i + 1];
    const seg = this.cum[i + 1] - this.cum[i];
    let pos = lerp(a, b, seg > 0 ? (along - this.cum[i]) / seg : 0);
    if (this.opts.jitter > 0) pos = addJitter(pos, this.opts.jitter);
    return {
      position: pos,
      accuracy: Math.max(5, this.opts.jitter),
      speed,
      heading: speed > 0 ? bearing(a, b) : null,
      timestamp: Date.now(),
      source: 'sim',
    };
  }
}

function addJitter(p: LngLat, meters: number): LngLat {
  const r = meters * Math.sqrt(Math.random());
  const th = Math.random() * 2 * Math.PI;
  const dLat = (r * Math.cos(th)) / 111_320;
  const dLng = (r * Math.sin(th)) / (111_320 * Math.cos((p[1] * Math.PI) / 180));
  return [p[0] + dLng, p[1] + dLat];
}
